import {Dispatch} from 'react';

import {memoize} from 'lodash';

import {trackEvent} from '~/shared/services/analytics';
import apiService from '~/shared/services/apiService';
import {FormNamesEnum} from '~/shared/services/analytics/analyticsModels';
import {createLogger} from '~/shared/logging';
import {getLocalizationService} from '~/shared/services/localisationService';
import {
  AuthFlowStep,
  LOGIN_PROVIDER_ANALYTICS_MAP,
  LOGIN_SCREEN_TYPES,
  LoginProvider,
} from '~/shared/components/SignIn/consts';
import {getCurrentLocation, pushRoute} from '~/shared/router';
import {navigateToDefaultStartPage} from '~/shared/services/navigation';

const logger = createLogger('onSubmitEmailHandler');

const isErrorWithMessage = (error: unknown): error is {message: string} =>
  !!error &&
  typeof error === 'object' &&
  'message' in error &&
  typeof error.message === 'string';

type OnSubmitEmailHandlerDeps = {
  setResErrors?: (errors: string | null) => void;
  email?: string;
  setEmailEntry?: (email: string) => void;
  shouldSendTrackEvent: boolean;
  linkType?: string;
  fromCheckout?: boolean;
  loginProvider: LoginProvider;
  setAuthFlowStep: Dispatch<AuthFlowStep>;
  setCurrentLoginScreen?: (screen: string) => void;
  setVerificationDetails?: (args: {
    authenticationToken: string;
  }) => void;
};

export const onSubmitEmailHandler = async ({
  setResErrors,
  email,
  setEmailEntry,
  shouldSendTrackEvent,
  linkType,
  setCurrentLoginScreen,
  setVerificationDetails,
  fromCheckout,
  loginProvider,
  setAuthFlowStep,
}: OnSubmitEmailHandlerDeps): Promise<{
  isPasswordScreen: boolean;
} | undefined> => {
  const {t} = getLocalizationService();

  setResErrors?.(null);
  if (!email) {
    logger.error('onSubmitEmail received empty email', email);
    return;
  }

  setEmailEntry?.(email);
  if (shouldSendTrackEvent) {
    // preventing re-sending auth code to send login track.
    trackEvent('hasClickedLogin', {linkType});
  }

  try {
    const {data} = await apiService.getUserAuthenticationDataAndSendAuthenticationCodeToUser({email});

    const isPasswordScreen = data.authenticationMethod === 'Password';
    setCurrentLoginScreen?.(isPasswordScreen ? LOGIN_SCREEN_TYPES.password : LOGIN_SCREEN_TYPES.verification);
    setAuthFlowStep(isPasswordScreen ? AuthFlowStep.PASSWORD : AuthFlowStep.OTP);

    setVerificationDetails?.({
      authenticationToken: data.codeAuthenticationData.authenticationToken,
    });

    return {
      isPasswordScreen,
    };
  } catch (error) {
    const formErrorType = isErrorWithMessage(error) ? error?.message : t('something_went_wrong');
    trackEvent('hasFormError', {
      formType: '2FA Login Form',
      formErrorType,
      linkType,
    });
    trackEvent('formErrorMessageView', {
      formName: FormNamesEnum.LOGIN,
      errorType: formErrorType,
      onCheckout: fromCheckout,
      authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider],
    });
    const errorType = 'Error while fetching getUserAuthenticationDataAndSendAuthenticationCodeToUser';
    logger.error(errorType, {email, e: error, fingerprint: errorType});
    setResErrors?.(formErrorType);
  }
};

export const checkFreeEmail = memoize(async (emailAddress: string) => {
  try {
    const response = await apiService.isEmailFree({emailAddress});
    return response?.data;
  } catch (error) {
    return false;
  }
}, (emailAddress: string) => emailAddress);

export const handleSuccessAuthNavigation = ({pageView, closeModal}: {
  pageView?: boolean;
  closeModal?: () => void;
}) => {
  if (!pageView && closeModal) {
    closeModal();
    return;
  }

  const {query} = getCurrentLocation();

  const returnUrl = query.successReturnUrl || query.returnUrl;

  if (returnUrl) {
    pushRoute(returnUrl);
    return;
  }
  navigateToDefaultStartPage({replace: true});
};
